import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate, useLocation } from 'react-router-dom';
import UserSearch from '../components/social/UserSearch';
import FriendList from '../components/social/FriendList';
import { LoadingSpinner } from '../components/ui/Icons';
import { QRCodeSVG } from 'qrcode.react';
import { Users, UserPlus, Bell, MessageCircle, Link } from 'lucide-react';

/* ─────────────────────────────────────────────────────────────
   Share-Link Panel – zeigt QR-Code, Copy-Button, Share-Button
 ───────────────────────────────────────────────────────────── */
const MyShareLink = ({ user }) => {
    const { showToast } = useToast();
    const [copied, setCopied] = useState(false);

    const profileSlug = user?.username?.toLowerCase().replace(/\s+/g, '-') || user?.username;
    const shareUrl = `${window.location.origin}/profile/${profileSlug}`;

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(shareUrl);
            setCopied(true);
            showToast('Link kopiert! 🔗', 'success');
            setTimeout(() => setCopied(false), 2000);
        } catch {
            showToast('Kopieren fehlgeschlagen – bitte manuell kopieren', 'error');
        }
    };

    const handleNativeShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: `${user?.username || 'Ich'} auf Plantlix`,
                    text: `Vernetze dich mit mir auf Plantlix – der Lern-App für GaLaBau! 🌿`,
                    url: shareUrl,
                });
            } catch (e) {
                if (e.name !== 'AbortError') {
                    showToast('Teilen fehlgeschlagen', 'error');
                }
            }
        }
    };

    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(`Hey! Vernetze dich mit mir auf Plantlix 🌿\n${shareUrl}`)}`;
    const telegramUrl = `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent('Vernetze dich mit mir auf Plantlix! 🌿')}`;

    return (
        <div className="max-w-lg mx-auto py-6">
            {/* Headline */}
            <div className="text-center mb-8">
                <div className="text-5xl mb-3">🔗</div>
                <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-1">
                    Dein Einladungs-Link
                </h2>
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                    Teile diesen Link oder QR-Code – Freunde können direkt dein Profil aufrufen und eine Anfrage senden.
                </p>
            </div>

            {/* QR-Code Section */}
            <div className="flex flex-col items-center mb-8">
                <div className="relative group">
                    {/* Glowing effect background */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-plantlix-400 to-emerald-400 rounded-[2.5rem] blur-2xl opacity-20 group-hover:opacity-30 transition-opacity duration-500" />
                    
                    <div className="relative p-8 bg-white dark:bg-white/[0.05] rounded-[2.5rem] shadow-2xl border border-gray-100 dark:border-white/10 overflow-hidden backdrop-blur-xl">
                        <div className="absolute top-0 left-0 w-full h-1.5 bg-gradient-to-r from-plantlix-400 via-emerald-400 to-plantlix-500" />
                        
                        <div className="bg-white p-4 rounded-3xl shadow-[inset_0_2px_10px_rgba(0,0,0,0.05)] border border-gray-100">
                            <QRCodeSVG
                                value={shareUrl}
                                size={260}
                                fgColor="#0f172a"
                                bgColor="#ffffff"
                                level="H"
                                includeMargin={false}
                                style={{ borderRadius: '16px' }}
                                className="w-full h-auto max-w-[260px] md:max-w-[280px]"
                            />
                        </div>
                    </div>
                </div>
                
                <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-gray-100 dark:bg-white/5 rounded-full border border-gray-200 dark:border-white/5">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-plantlix-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-plantlix-500"></span>
                    </span>
                    <span className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-tighter">Live QR-Code</span>
                </div>
            </div>

            {/* URL-Box + Copy */}
            <div className="flex items-center gap-2 mb-5 bg-gray-100 dark:bg-gray-800 rounded-xl p-3 border border-gray-200 dark:border-gray-700">
                <span className="flex-1 font-mono text-xs text-gray-700 dark:text-gray-300 truncate select-all">
                    {shareUrl}
                </span>
                <button
                    onClick={handleCopy}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                        copied
                            ? 'bg-green-500 text-white'
                            : 'bg-plantlix-500 hover:bg-plantlix-600 text-white'
                    }`}
                >
                    {copied ? '✓ Kopiert' : 'Kopieren'}
                </button>
            </div>

            {/* Share Buttons */}
            <div className="flex flex-col gap-3">
                {/* Native Share (iOS/Android) */}
                {typeof navigator !== 'undefined' && navigator.share && (
                    <button
                        onClick={handleNativeShare}
                        className="flex items-center justify-center gap-3 w-full py-3.5 rounded-xl bg-plantlix-500 hover:bg-plantlix-600 text-white font-bold text-sm transition-all shadow-md shadow-plantlix-500/20 active:scale-[0.98]"
                    >
                        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
                            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        Teilen…
                    </button>
                )}

                {/* WhatsApp */}
                <a
                    href={whatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full py-3.5 rounded-xl bg-[#25D366] hover:bg-[#1ebe5a] text-white font-bold text-sm transition-all shadow-md active:scale-[0.98]"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                        </svg>
                        Via WhatsApp teilen
                    </a>

                {/* Telegram */}
                <a
                    href={telegramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 w-full py-3.5 rounded-xl bg-[#2AABEE] hover:bg-[#1a9bd9] text-white font-bold text-sm transition-all shadow-md active:scale-[0.98]"
                >
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/>
                    </svg>
                    Via Telegram teilen
                </a>
            </div>

            <p className="text-center text-xs text-gray-400 dark:text-gray-600 mt-5">
                💡 Der QR-Code kann mit jedem Smartphone-Scanner geöffnet werden.
            </p>
        </div>
    );
};

/* ─────────────────────────────────────────────────────────────
   Haupt-Komponente Friends
 ───────────────────────────────────────────────────────────── */
const Friends = () => {
    const { fetchWithAuth, user } = useContext(AuthContext);
    const { showToast } = useToast();
    const navigate = useNavigate();
    const location = useLocation();

    const getInitialTab = () => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        return ['friends', 'requests', 'add', 'share'].includes(tab) ? tab : 'friends';
    };

    const [activeTab, setActiveTab] = useState(getInitialTab);
    const [friends, setFriends] = useState([]);
    const [pendingSent, setPendingSent] = useState([]);
    const [pendingReceived, setPendingReceived] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadFriendships();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tab = params.get('tab');
        if (tab && ['friends', 'requests', 'add', 'share'].includes(tab)) setActiveTab(tab);
    }, [location.search]);

    const loadFriendships = async () => {
        setLoading(true);
        try {
            const resp = await fetchWithAuth('/api/friendships/');
            if (resp.ok) {
                const data = await resp.json();
                setFriends(data.friends || []);
                setPendingSent(data.pending_sent || []);
                setPendingReceived(data.pending_received || []);
            }
        } catch (err) {
            showToast('Fehler beim Laden der Freunde', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleSendRequest = async (username) => {
        try {
            const resp = await fetchWithAuth('/api/friendships/request/', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username })
            });
            const data = await resp.json();
            if (resp.ok) {
                showToast(`Freundschaftsanfrage an ${username} gesendet! ✅`, 'success');
                loadFriendships();
            } else {
                showToast(data.error || 'Fehler beim Senden der Anfrage', 'error');
            }
        } catch (err) {
            showToast('Fehler beim Senden der Anfrage', 'error');
        }
    };

    const respondToRequest = async (friendshipId, accept) => {
        try {
            const resp = await fetchWithAuth(`/api/friendships/${friendshipId}/response/`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ accept })
            });
            if (resp.ok) {
                showToast(accept ? 'Freundschaftsanfrage angenommen! 👥' : 'Anfrage abgelehnt', 'success');
                loadFriendships();
            } else {
                const data = await resp.json();
                showToast(data.error || 'Fehler', 'error');
            }
        } catch (err) {
            showToast('Fehler', 'error');
        }
    };

    const unfriend = async (friendshipId, username) => {
        if (!confirm(`${username} wirklich entfreunden?`)) return;
        try {
            const resp = await fetchWithAuth(`/api/friendships/${friendshipId}/delete/`, { method: 'DELETE' });
            if (resp.ok) {
                showToast('Freundschaft beendet', 'success');
                loadFriendships();
            } else {
                showToast('Fehler', 'error');
            }
        } catch (err) {
            showToast('Fehler', 'error');
        }
    };

    const tabs = [
        { id: 'friends', label: 'Freunde', icon: Users, count: friends.length },
        { id: 'requests', label: 'Anfragen', icon: Bell, count: pendingReceived.length },
        { id: 'add', label: 'Hinzufügen', icon: UserPlus, count: null },
        { id: 'share', label: '🔗 Mein Link', icon: Link, count: null },
    ];

    if (loading && !friends.length && !pendingReceived.length) {
        return (
            <div className="flex justify-center items-center min-h-[60vh]">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-[#0d0d0d] transition-colors duration-300">
            {/* ── Hero Header ── */}
            <div className="bg-white dark:bg-[#141414] border-b border-gray-100 dark:border-white/5">
                <div className="max-w-4xl mx-auto px-5 py-8">
                    <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-xl mb-3 border border-plantlix-100 dark:border-plantlix-800/30">
                                <span className="w-2 h-2 rounded-full bg-plantlix-500" />
                                <span className="text-[11px] font-black uppercase tracking-widest text-plantlix-600 dark:text-plantlix-400">Community</span>
                            </div>
                            <h1 className="text-3xl font-black text-gray-900 dark:text-white tracking-tight mb-1">Freunde & Netzwerk</h1>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">Vernetze dich, vergleiche deine Erfolge und lerne gemeinsam.</p>
                        </div>

                        {/* Stats Pills */}
                        <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 px-4 py-2.5 bg-plantlix-50 dark:bg-plantlix-900/20 rounded-2xl border border-plantlix-100 dark:border-plantlix-800/30">
                                <Users className="w-4 h-4 text-plantlix-500" />
                                <span className="font-black text-plantlix-700 dark:text-plantlix-300 text-sm">{friends.length}</span>
                                <span className="text-xs text-plantlix-600/70 dark:text-plantlix-400/70 font-medium">Freunde</span>
                            </div>
                            {pendingReceived.length > 0 && (
                                <div className="flex items-center gap-2 px-4 py-2.5 bg-amber-50 dark:bg-amber-900/20 rounded-2xl border border-amber-100 dark:border-amber-800/30">
                                    <Bell className="w-4 h-4 text-amber-500" />
                                    <span className="font-black text-amber-700 dark:text-amber-300 text-sm">{pendingReceived.length}</span>
                                    <span className="text-xs text-amber-600/70 dark:text-amber-400/70 font-medium">Anfragen</span>
                                </div>
                            )}
                            <button
                                onClick={() => setActiveTab('share')}
                                className="flex items-center gap-2 px-4 py-2.5 bg-white dark:bg-white/5 rounded-2xl border border-gray-200 dark:border-white/10 hover:border-plantlix-300 dark:hover:border-plantlix-700 text-gray-600 dark:text-gray-300 hover:text-plantlix-600 dark:hover:text-plantlix-400 transition-all text-sm font-bold"
                            >
                                <Link className="w-4 h-4" />
                                Einladen
                            </button>
                        </div>
                    </div>

                    {/* Tabs - Full width, evenly distributed */}
                    <div className="flex gap-1 mt-6 bg-gray-100 dark:bg-white/5 p-1 rounded-2xl w-full">
                        {tabs.map(tab => (
                            <button
                                key={tab.id}
                                onClick={() => {
                                    setActiveTab(tab.id);
                                    navigate(`/friends?tab=${tab.id}`, { replace: true });
                                }}
                                className={`flex-1 flex items-center justify-center gap-1 py-2.5 px-2 rounded-xl text-[11px] font-bold transition-all min-w-0 ${
                                    activeTab === tab.id
                                        ? 'bg-white dark:bg-white/10 text-plantlix-600 dark:text-plantlix-400 shadow-sm'
                                        : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                                }`}
                            >
                                <tab.icon className="w-3.5 h-3.5 flex-shrink-0" />
                                <span className="truncate">{tab.label}</span>
                                {tab.count > 0 && (
                                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded-full flex-shrink-0 ${
                                        activeTab === tab.id
                                            ? 'bg-plantlix-100 dark:bg-plantlix-900/40 text-plantlix-600 dark:text-plantlix-400'
                                            : 'bg-gray-200 dark:bg-white/10 text-gray-600 dark:text-gray-300'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── Content ── */}
            <div className="max-w-4xl mx-auto px-5 py-8">
                {/* Friends Tab */}
                {activeTab === 'friends' && (
                    <FriendList friends={friends} onUnfriend={unfriend} />
                )}

                {/* Requests Tab */}
                {activeTab === 'requests' && (
                    <div className="space-y-8 max-w-2xl">
                        {/* Incoming */}
                        <div>
                            <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4 flex items-center gap-2">
                                <Bell className="w-3.5 h-3.5" />
                                Eingehende Anfragen
                                {pendingReceived.length > 0 && (
                                    <span className="bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 text-[10px] font-black px-2 py-0.5 rounded-full">
                                        {pendingReceived.length}
                                    </span>
                                )}
                            </h3>
                            {pendingReceived.length === 0 ? (
                                <div className="py-8 text-center bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-100 dark:border-white/5">
                                    <div className="text-3xl mb-2 opacity-30">📭</div>
                                    <p className="text-sm text-gray-400">Keine neuen Anfragen.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {pendingReceived.map(req => (
                                        <div key={req.friendship_id} className="flex items-center gap-4 p-4 bg-white dark:bg-white/[0.03] rounded-2xl border border-amber-100 dark:border-amber-800/30 shadow-sm">
                                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                                                {req.username.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-bold text-gray-900 dark:text-white truncate">{req.username}</p>
                                                <p className="text-xs text-gray-400">möchte mit dir befreundet sein</p>
                                            </div>
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => respondToRequest(req.friendship_id, true)}
                                                    className="px-4 py-2 bg-plantlix-500 hover:bg-plantlix-600 text-white rounded-xl font-bold text-sm transition-all active:scale-95 shadow-md shadow-plantlix-500/20"
                                                >
                                                    Annehmen
                                                </button>
                                                <button
                                                    onClick={() => respondToRequest(req.friendship_id, false)}
                                                    className="px-4 py-2 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10 text-gray-600 dark:text-gray-400 rounded-xl font-bold text-sm transition-all active:scale-95"
                                                >
                                                    Ablehnen
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Sent */}
                        <div>
                            <h3 className="text-xs font-black text-gray-400 dark:text-gray-500 uppercase tracking-[0.2em] mb-4">Gesendete Anfragen</h3>
                            {pendingSent.length === 0 ? (
                                <div className="py-8 text-center bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-100 dark:border-white/5">
                                    <p className="text-sm text-gray-400">Keine ausstehenden Anfragen.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {pendingSent.map(req => (
                                        <div key={req.friendship_id} className="flex items-center gap-4 p-4 bg-white dark:bg-white/[0.03] rounded-2xl border border-gray-100 dark:border-white/5 opacity-70">
                                            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gray-300 to-gray-400 dark:from-gray-600 dark:to-gray-700 flex items-center justify-center text-white font-black text-lg flex-shrink-0">
                                                {req.username.charAt(0).toUpperCase()}
                                            </div>
                                            <div className="flex-1">
                                                <p className="font-bold text-gray-700 dark:text-gray-300">{req.username}</p>
                                            </div>
                                            <span className="text-[11px] font-black text-gray-400 uppercase tracking-wider px-3 py-1.5 bg-gray-50 dark:bg-white/5 rounded-xl border border-gray-100 dark:border-white/5">
                                                Ausstehend...
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Add Friend Tab */}
                {activeTab === 'add' && (
                    <div className="max-w-lg">
                        <div className="mb-8">
                            <h2 className="text-xl font-black text-gray-900 dark:text-white mb-1 tracking-tight">Jemanden finden</h2>
                            <p className="text-gray-500 dark:text-gray-400 text-sm">Suche nach Nutzernamen, um eine Freundschaftsanfrage zu senden.</p>
                        </div>
                        <UserSearch onSendRequest={handleSendRequest} />
                        
                        {/* Hinweis auf Share-Link */}
                        <div className="mt-8 p-4 rounded-2xl bg-plantlix-50 dark:bg-plantlix-900/20 border border-plantlix-100 dark:border-plantlix-800/30 flex items-start gap-3">
                            <span className="text-2xl">💡</span>
                            <div>
                                <p className="text-sm font-semibold text-plantlix-700 dark:text-plantlix-300">Einfacher: Deinen Link teilen!</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Statt zu suchen kannst du deinen persönlichen Einladungslink oder QR-Code teilen.{' '}
                                    <button onClick={() => setActiveTab('share')} className="text-plantlix-600 dark:text-plantlix-400 font-bold underline underline-offset-2">
                                        Zum Link →
                                    </button>
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Share Tab */}
                {activeTab === 'share' && (
                    <MyShareLink user={user} />
                )}
            </div>
        </div>
    );
};

export default Friends;
